/**
 * G2eGente - Pesquisas de Clima Organizacional
 * Lista e gerencia pesquisas de clima da empresa
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { climateSurveyAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Plus,
  BarChart3,
  FileText,
  Users,
  Calendar,
  Loader2,
  Play,
  Pause,
  Eye,
  Pencil,
  Trash2,
  ThermometerSun,
  ArrowLeft,
  Link2,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700', icon: FileText },
  ativa: { label: 'Ativa', color: 'bg-emerald-100 text-emerald-700', icon: Play },
  encerrada: { label: 'Encerrada', color: 'bg-slate-200 text-slate-600', icon: Pause }
};

export default function ClimateSurveys() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todas');
  const [copiedLink, setCopiedLink] = useState(null);

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      const { data } = await climateSurveyAPI.list();
      setSurveys(data);
    } catch (error) {
      toast.error('Erro ao carregar pesquisas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir esta pesquisa? Todas as respostas serão perdidas.')) return;
    
    try {
      await climateSurveyAPI.delete(id);
      toast.success('Pesquisa excluída');
      fetchSurveys();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleActivate = async (survey, e) => {
    e.stopPropagation();
    try {
      await climateSurveyAPI.update(survey.id, { status: 'ativa' });
      toast.success('Pesquisa ativada! Os funcionários já podem responder.');
      fetchSurveys();
    } catch (error) {
      toast.error('Erro ao ativar pesquisa');
    }
  };

  const handleClose = async (survey, e) => {
    e.stopPropagation();
    try {
      await climateSurveyAPI.update(survey.id, { status: 'encerrada' });
      toast.success('Pesquisa encerrada');
      fetchSurveys();
    } catch (error) {
      toast.error('Erro ao encerrar pesquisa');
    }
  };
  
  const handleCopyLink = (codigo, e) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/p/clima/${codigo}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(codigo);
    toast.success('Link copiado! Compartilhe com quem desejar.');
    setTimeout(() => setCopiedLink(null), 3000);
  };

  const filteredSurveys = filter === 'todas' 
    ? surveys 
    : surveys.filter(s => s.status === filter);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/company')} data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ThermometerSun className="w-7 h-7 text-cyan-600" />
              Pesquisa de Clima
            </h1>
            <p className="text-slate-500 mt-1">Gerencie pesquisas de clima organizacional</p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/company/climate-surveys/new')}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="new-survey-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Pesquisa
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: 'todas', label: 'Todas' },
          { key: 'ativa', label: 'Ativas' },
          { key: 'rascunho', label: 'Rascunhos' },
          { key: 'encerrada', label: 'Encerradas' }
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'bg-cyan-600' : ''}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Empty State */}
      {filteredSurveys.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mb-4">
              <ThermometerSun className="w-8 h-8 text-cyan-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {filter === 'todas' ? 'Nenhuma pesquisa criada' : `Nenhuma pesquisa ${filter}`}
            </h3>
            <p className="text-slate-500 text-center max-w-md mb-4">
              Pesquisas de clima ajudam a entender como os funcionários se sentem 
              sobre o ambiente de trabalho de forma anônima e segura.
            </p>
            {filter === 'todas' && (
              <Button onClick={() => navigate('/company/climate-surveys/new')} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeira Pesquisa
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Survey Cards */}
      <div className="grid gap-4">
        {filteredSurveys.map(survey => {
          const statusConfig = STATUS_CONFIG[survey.status] || STATUS_CONFIG.rascunho;
          const StatusIcon = statusConfig.icon;
          const participacao = survey.total_elegivel > 0 
            ? Math.round((survey.total_respostas / survey.total_elegivel) * 100)
            : 0;

          return (
            <Card 
              key={survey.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                // Rascunho ou ativa sem respostas: vai para edição
                // Ativa com respostas ou encerrada: vai para resultados
                const canEdit = survey.status === 'rascunho' || (survey.status === 'ativa' && survey.total_respostas === 0);
                navigate(canEdit 
                  ? `/company/climate-surveys/${survey.id}/edit` 
                  : `/company/climate-surveys/${survey.id}/results`
                );
              }}
              data-testid={`survey-card-${survey.id}`}
            >
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-800">{survey.titulo}</h3>
                      <Badge className={statusConfig.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                    
                    {survey.descricao && (
                      <p className="text-slate-500 text-sm mb-3 line-clamp-2">{survey.descricao}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {survey.perguntas?.length || 0} perguntas
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {survey.total_respostas}/{survey.total_elegivel} respostas ({participacao}%)
                      </span>
                      {survey.data_fim && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Até {new Date(survey.data_fim).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {survey.status === 'rascunho' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/company/climate-surveys/${survey.id}/edit`); }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={(e) => handleActivate(survey, e)}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Ativar
                        </Button>
                      </>
                    )}
                    
                    {survey.status === 'ativa' && (
                      <>
                        {/* Botão Editar - só aparece se não tem respostas */}
                        {survey.total_respostas === 0 && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); navigate(`/company/climate-surveys/${survey.id}/edit`); }}
                            title="Editar pesquisa (disponível enquanto não houver respostas)"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {survey.link_publico && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={(e) => handleCopyLink(survey.link_publico, e)}
                            data-testid={`copy-link-${survey.id}`}
                          >
                            {copiedLink === survey.link_publico ? (
                              <Check className="w-4 h-4 mr-1" />
                            ) : (
                              <Link2 className="w-4 h-4 mr-1" />
                            )}
                            {copiedLink === survey.link_publico ? 'Copiado!' : 'Link Público'}
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/company/climate-surveys/${survey.id}/results`); }}
                        >
                          <BarChart3 className="w-4 h-4 mr-1" />
                          Resultados
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          onClick={(e) => handleClose(survey, e)}
                        >
                          <Pause className="w-4 h-4 mr-1" />
                          Encerrar
                        </Button>
                      </>
                    )}
                    
                    {survey.status === 'encerrada' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/company/climate-surveys/${survey.id}/results`); }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Resultados
                      </Button>
                    )}
                    
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDelete(survey.id, e)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Card */}
      <Card className="mt-6 bg-cyan-50 border-cyan-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
              <ThermometerSun className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h4 className="font-semibold text-cyan-800 mb-1">Sobre a Pesquisa de Clima</h4>
              <p className="text-sm text-cyan-700">
                As respostas são <strong>100% anônimas</strong> e em conformidade com a LGPD. 
                Os gráficos são gerados a partir de 2 respostas. Os funcionários respondem 
                pelo aplicativo móvel de forma confidencial.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
